import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { Table } from "./Table.js";

const CANVAS_WIDTH = 750;
const CANVAS_HEIGHT = 400;

const SNAP_DISTANCE = 14;
const CENTER_SNAP_DISTANCE = 9;
const SNAP_GAP = 3;
const SNAP_OVERLAP_TOLERANCE = 22;

const FloorScroll = styled.div`
  width: 100%;
  flex: 1;
  min-height: 300px;
  overflow: auto;
  border: none;
  border-radius: 10px;
  background: transparent;

  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f6eee5;
    border-radius: 999px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c7b7ad;
    border-radius: 999px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #aa9990;
  }
`;

const FloorCanvasViewport = styled.div`
  width: ${({ $zoom }) => `${CANVAS_WIDTH * $zoom}px`};
  height: ${({ $zoom }) => `${CANVAS_HEIGHT * $zoom}px`};
  position: relative;
`;

const FloorCanvas = styled.div`
  width: ${CANVAS_WIDTH}px;
  height: ${CANVAS_HEIGHT}px;
  position: relative;
  border-radius: 10px;
  background-color: #fffdf9;
  background-image:
    linear-gradient(to right, rgba(75, 51, 53, 0.035) 1px, transparent 1px),
    linear-gradient(to bottom, rgba(75, 51, 53, 0.035) 1px, transparent 1px);
  background-size: 75px 75px;
  transform: scale(${({ $zoom }) => $zoom});
  transform-origin: top left;
`;

const PositionedTable = styled.div`
  position: absolute;
  top: ${({ top }) => Number(top) || 0}px;
  left: ${({ left }) => Number(left) || 0}px;
  width: ${({ width }) => Number(width) || 50}px;
  height: ${({ height }) => Number(height) || 50}px;
  display: ${({ isPlaced }) => (isPlaced ? "block" : "none")};
  cursor: ${({ enableMove }) => (enableMove ? "move" : "pointer")};
  outline: ${({ isDragging }) =>
    isDragging ? "2px solid rgba(238, 117, 76, 0.75)" : "none"};
  outline-offset: 2px;
  border-radius: 8px;
  z-index: ${({ isDragging }) => (isDragging ? 20 : 1)};
`;

const SnapGuide = styled.div`
  position: absolute;
  left: ${({ left }) => Number(left) || 0}px;
  top: ${({ top }) => Number(top) || 0}px;
  width: ${({ width }) => Number(width) || 50}px;
  height: ${({ height }) => Number(height) || 50}px;
  border: 1px solid rgba(238, 117, 76, 0.75);
  background: rgba(238, 117, 76, 0.08);
  box-shadow: 0 0 0 3px rgba(238, 117, 76, 0.1);
  border-radius: 8px;
  pointer-events: none;
  z-index: 15;
`;

const ZoomControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 8px 2px 0 2px;
  font-size: 0.78rem;
  color: #6d625d;
`;

const ZoomButton = styled.button`
  width: 26px;
  height: 26px;
  border-radius: 7px;
  border: 1px solid #e2d8cf;
  background: #fff;
  color: #4b3335;
  font-weight: 800;
  cursor: pointer;

  &:hover {
    border-color: #ee754c;
    color: #ee754c;
  }
`;

const ZoomSlider = styled.input`
  width: 150px;
  accent-color: #ee754c;
`;

const ZoomPercent = styled.span`
  min-width: 42px;
  text-align: right;
  font-weight: 750;
  color: #6d625d;
`;

const EmptyState = styled.div`
  padding: 22px;
  color: #7a6d68;
  text-align: center;
  border: 1px dashed #e3d8cf;
  border-radius: 12px;
  background: #fffaf4;
  font-weight: 650;
`;

const UnplacedBox = styled.div`
  margin-top: 10px;
  padding: 10px;
  border: 1px dashed #e3d8cf;
  border-radius: 12px;
  background: #fffaf4;
`;

const UnplacedTitle = styled.div`
  font-size: 0.82rem;
  font-weight: 800;
  color: #6d625d;
  margin-bottom: 8px;
`;

const UnplacedList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const UnplacedButton = styled.button`
  border: 1px solid ${({ selected }) => (selected ? "#EE754C" : "#d8cec5")};
  background: ${({ selected, status }) => {
    if (selected && status === "1") return "#EE561B";
    if (selected) return "#EE754C";
    if (status === "1") return "#99B0AD";
    return "#ffffff";
  }};
  color: ${({ selected, status }) =>
    selected || status === "1" ? "white" : "#4B3335"};
  border-radius: 999px;
  padding: 6px 11px;
  font-size: 0.82rem;
  font-weight: 750;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: #ee754c;
    transform: translateY(-1px);
  }
`;

function buildAvailabilityMap(availabilityTables) {
  const map = new Map();

  (availabilityTables || []).forEach((table) => {
    if (!table?.number) return;

    map.set(String(table.number), {
      number: String(table.number),
      status: String(table.status ?? "0"),
      reservationIds: table.reservationIds || [],
      reservationDetails: table.reservationDetails || [],
    });
  });

  return map;
}

function buildLayoutMap(layoutItems) {
  const map = new Map();

  (layoutItems || []).forEach((item) => {
    const tableId = item?.table_id || item?.tableId;

    if (!tableId) return;

    map.set(String(tableId), item);
  });

  return map;
}

function applyLayoutItem(table, layoutItem) {
  if (!layoutItem) return table;

  return {
    ...table,
    left: layoutItem.left,
    top: layoutItem.top,
    width: layoutItem.width,
    height: layoutItem.height,
    isPlaced: layoutItem.isPlaced,
    rotation: layoutItem.rotation ?? table.rotation ?? 0,
  };
}

function isTableSelected(selectedTables, zoneName, tableId) {
  return !!selectedTables?.[zoneName]?.some(
    (table) => String(table.number) === String(tableId),
  );
}

function getTableLabel(table) {
  return table?.table_name || table?.table_number || `#${table?.id}`;
}

function getReservationInfoText(availabilityInfo) {
  const reservationDetails = availabilityInfo?.reservationDetails || [];

  if (reservationDetails.length > 0) {
    return reservationDetails
      .map((reservation) => {
        const timeText = reservation.estimatedtime
          ? `${reservation.time}-${reservation.estimatedtime}`
          : reservation.time;

        return `${reservation.full_name || "Reserva"} (#${
          reservation.id
        }) ${timeText}`;
      })
      .join(" | ");
  }

  if (!availabilityInfo?.reservationIds?.length) {
    return "";
  }

  return `Ocupada pela reserva #${availabilityInfo.reservationIds.join(", #")}`;
}

function clampPosition(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function rangesOverlapWithTolerance(aStart, aEnd, bStart, bEnd, tolerance) {
  return aEnd + tolerance >= bStart && bEnd + tolerance >= aStart;
}

function getRectFromTable(table) {
  const left = Number(table?.left) || 0;
  const top = Number(table?.top) || 0;
  const width = Number(table?.width) || 50;
  const height = Number(table?.height) || 50;

  return {
    left,
    top,
    width,
    height,
    right: left + width,
    bottom: top + height,
    centerX: left + width / 2,
    centerY: top + height / 2,
  };
}

function getRectFromPosition(left, top, width, height) {
  return {
    left,
    top,
    width,
    height,
    right: left + width,
    bottom: top + height,
    centerX: left + width / 2,
    centerY: top + height / 2,
  };
}

function getBestSnapPosition({
  tableId,
  proposedLeft,
  proposedTop,
  width,
  height,
  otherTables,
}) {
  let snappedLeft = proposedLeft;
  let snappedTop = proposedTop;

  let bestX = {
    distance: Infinity,
    value: proposedLeft,
    guide: null,
  };

  let bestY = {
    distance: Infinity,
    value: proposedTop,
    guide: null,
  };

  const movingRect = getRectFromPosition(
    proposedLeft,
    proposedTop,
    width,
    height,
  );

  (otherTables || []).forEach((otherTable) => {
    if (!otherTable?.isPlaced) return;
    if (String(otherTable.id) === String(tableId)) return;

    const otherRect = getRectFromTable(otherTable);

    const verticalOverlap = rangesOverlapWithTolerance(
      movingRect.top,
      movingRect.bottom,
      otherRect.top,
      otherRect.bottom,
      SNAP_OVERLAP_TOLERANCE,
    );

    const horizontalOverlap = rangesOverlapWithTolerance(
      movingRect.left,
      movingRect.right,
      otherRect.left,
      otherRect.right,
      SNAP_OVERLAP_TOLERANCE,
    );

    const guide = {
      left: otherRect.left,
      top: otherRect.top,
      width: otherRect.width,
      height: otherRect.height,
    };

    if (verticalOverlap) {
      const leftAttachValue = otherRect.right + SNAP_GAP;
      const leftAttachDistance = Math.abs(proposedLeft - leftAttachValue);

      if (
        leftAttachDistance <= SNAP_DISTANCE &&
        leftAttachDistance < bestX.distance
      ) {
        bestX = {
          distance: leftAttachDistance,
          value: leftAttachValue,
          guide,
        };
      }

      const rightAttachValue = otherRect.left - width - SNAP_GAP;
      const rightAttachDistance = Math.abs(proposedLeft - rightAttachValue);

      if (
        rightAttachDistance <= SNAP_DISTANCE &&
        rightAttachDistance < bestX.distance
      ) {
        bestX = {
          distance: rightAttachDistance,
          value: rightAttachValue,
          guide,
        };
      }
    }

    if (horizontalOverlap) {
      const topAttachValue = otherRect.bottom + SNAP_GAP;
      const topAttachDistance = Math.abs(proposedTop - topAttachValue);

      if (
        topAttachDistance <= SNAP_DISTANCE &&
        topAttachDistance < bestY.distance
      ) {
        bestY = {
          distance: topAttachDistance,
          value: topAttachValue,
          guide,
        };
      }

      const bottomAttachValue = otherRect.top - height - SNAP_GAP;
      const bottomAttachDistance = Math.abs(proposedTop - bottomAttachValue);

      if (
        bottomAttachDistance <= SNAP_DISTANCE &&
        bottomAttachDistance < bestY.distance
      ) {
        bestY = {
          distance: bottomAttachDistance,
          value: bottomAttachValue,
          guide,
        };
      }
    }

    const centerXValue = otherRect.centerX - width / 2;
    const centerXDistance = Math.abs(movingRect.centerX - otherRect.centerX);

    if (
      centerXDistance <= CENTER_SNAP_DISTANCE &&
      centerXDistance < bestX.distance
    ) {
      bestX = {
        distance: centerXDistance,
        value: centerXValue,
        guide,
      };
    }

    const centerYValue = otherRect.centerY - height / 2;
    const centerYDistance = Math.abs(movingRect.centerY - otherRect.centerY);

    if (
      centerYDistance <= CENTER_SNAP_DISTANCE &&
      centerYDistance < bestY.distance
    ) {
      bestY = {
        distance: centerYDistance,
        value: centerYValue,
        guide,
      };
    }
  });

  let snapGuide = null;

  if (bestX.distance !== Infinity) {
    snappedLeft = bestX.value;
    snapGuide = bestX.guide;
  }

  if (bestY.distance !== Infinity) {
    snappedTop = bestY.value;
    snapGuide = bestY.guide || snapGuide;
  }

  return {
    left: snappedLeft,
    top: snappedTop,
    guide: snapGuide,
  };
}

function mergeLayoutDraftItem(items = [], nextItem) {
  if (!nextItem?.table_id && !nextItem?.tableId) return items;

  const nextTableId = String(nextItem.table_id || nextItem.tableId);
  const nextItems = [...items];

  const existingIndex = nextItems.findIndex((item) => {
    const itemTableId = item?.table_id || item?.tableId;
    return String(itemTableId) === nextTableId;
  });

  if (existingIndex >= 0) {
    nextItems[existingIndex] = {
      ...nextItems[existingIndex],
      ...nextItem,
    };
  } else {
    nextItems.push(nextItem);
  }

  return nextItems;
}

export default function ReservationFloorPlan({
  allTables = [],
  zoneName,
  availabilityTables = [],
  selectedTables = {},
  onSelectTable,
  layoutOverrides = [],
  layoutDraftItems = [],
  enableMove = false,
  onLayoutItemChange,
}) {
  const [dragState, setDragState] = useState(null);
  const [snapGuide, setSnapGuide] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [liveDraftLayoutItems, setLiveDraftLayoutItems] = useState([]);

  const placedTablesRef = useRef([]);

  const updateZoom = (nextZoom) => {
    const safeZoom = Math.max(0.5, Math.min(1.8, Number(nextZoom) || 1));
    setZoom(safeZoom);
  };

  const availabilityById = useMemo(
    () => buildAvailabilityMap(availabilityTables),
    [availabilityTables],
  );

  const layoutOverrideByTableId = useMemo(
    () => buildLayoutMap(layoutOverrides),
    [layoutOverrides],
  );

  const layoutDraftByTableId = useMemo(() => {
    const combinedDraftItems = [
      ...(layoutDraftItems || []),
      ...(liveDraftLayoutItems || []),
    ];

    return buildLayoutMap(combinedDraftItems);
  }, [layoutDraftItems, liveDraftLayoutItems]);

  const zoneRealTables = useMemo(() => {
    const availableIds = new Set(Array.from(availabilityById.keys()));

    return (allTables || [])
      .filter((table) => availableIds.has(String(table.id)))
      .map((table) => {
        const override = layoutOverrideByTableId.get(String(table.id));
        const draft = layoutDraftByTableId.get(String(table.id));

        return applyLayoutItem(applyLayoutItem(table, override), draft);
      })
      .sort((a, b) => Number(a.table_number) - Number(b.table_number));
  }, [
    allTables,
    availabilityById,
    layoutOverrideByTableId,
    layoutDraftByTableId,
  ]);

  const placedTables = zoneRealTables.filter((table) => table.isPlaced);
  const unplacedTables = zoneRealTables.filter((table) => !table.isPlaced);

  useEffect(() => {
    placedTablesRef.current = placedTables;
  }, [placedTables]);

  useEffect(() => {
    if (!enableMove) {
      setLiveDraftLayoutItems([]);
      setDragState(null);
      setSnapGuide(null);
    }
  }, [enableMove]);

  const handleTableClick = (table) => {
    if (enableMove) return;
    if (!table?.id || typeof onSelectTable !== "function") return;

    const availabilityInfo = availabilityById.get(String(table.id)) || {
      number: String(table.id),
      status: "0",
      reservationIds: [],
      reservationDetails: [],
    };

    onSelectTable(availabilityInfo, zoneName);
  };

  const handleMoveMouseDown = (event, table) => {
    if (!enableMove || !table?.id) return;

    event.preventDefault();
    event.stopPropagation();

    setDragState({
      tableId: table.id,
      startMouseX: event.clientX,
      startMouseY: event.clientY,
      startLeft: Number(table.left) || 0,
      startTop: Number(table.top) || 0,
      width: Number(table.width) || 50,
      height: Number(table.height) || 50,
      table,
    });
  };

  useEffect(() => {
    if (!dragState) return;

    const handleMouseMove = (event) => {
      const dx = (event.clientX - dragState.startMouseX) / zoom;
      const dy = (event.clientY - dragState.startMouseY) / zoom;

      const maxLeft = CANVAS_WIDTH - dragState.width;
      const maxTop = CANVAS_HEIGHT - dragState.height;

      const rawLeft = clampPosition(
        Math.round(dragState.startLeft + dx),
        0,
        maxLeft,
      );

      const rawTop = clampPosition(
        Math.round(dragState.startTop + dy),
        0,
        maxTop,
      );

      const snapResult = getBestSnapPosition({
        tableId: dragState.tableId,
        proposedLeft: rawLeft,
        proposedTop: rawTop,
        width: dragState.width,
        height: dragState.height,
        otherTables: placedTablesRef.current,
      });

      const nextLeft = clampPosition(Math.round(snapResult.left), 0, maxLeft);
      const nextTop = clampPosition(Math.round(snapResult.top), 0, maxTop);

      setSnapGuide(snapResult.guide);
      const nextLayoutItem = {
        table_id: dragState.tableId,
        left: nextLeft,
        top: nextTop,
        width: dragState.width,
        height: dragState.height,
        isPlaced: true,
        rotation: dragState.table?.rotation ?? 0,
      };

      setLiveDraftLayoutItems((previousItems) =>
        mergeLayoutDraftItem(previousItems, nextLayoutItem),
      );

      if (typeof onLayoutItemChange === "function") {
        onLayoutItemChange(nextLayoutItem);
      }
    };
    const handlePointerUp = () => {
      setDragState(null);
      setSnapGuide(null);

      // Keep liveDragLayoutItem visible until parent draft/override catches up.
      // It will clear when leaving move mode or saving/discarding.
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handlePointerUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handlePointerUp);
    };
  }, [dragState, onLayoutItemChange, zoom]);

  if (!zoneName) {
    return <EmptyState>Seleciona uma sala para ver as mesas.</EmptyState>;
  }

  if (!zoneRealTables.length) {
    return <EmptyState>Sem mesas disponíveis nesta sala.</EmptyState>;
  }

  return (
    <>
      <FloorScroll>
        <FloorCanvasViewport $zoom={zoom}>
          <FloorCanvas $zoom={zoom}>
            {snapGuide && (
              <SnapGuide
                left={snapGuide.left}
                top={snapGuide.top}
                width={snapGuide.width}
                height={snapGuide.height}
              />
            )}

            {placedTables.map((table) => {
              const availabilityInfo =
                availabilityById.get(String(table.id)) || {};

              const selected = isTableSelected(
                selectedTables,
                zoneName,
                table.id,
              );

              const isDragging =
                dragState && Number(dragState.tableId) === Number(table.id);

              return (
                <PositionedTable
                  key={table.id}
                  top={table.top}
                  left={table.left}
                  width={table.width}
                  height={table.height}
                  isPlaced={table.isPlaced}
                  enableMove={enableMove}
                  isDragging={isDragging}
                  onMouseDown={(event) => handleMoveMouseDown(event, table)}
                >
                  <Table
                    table_name={table.table_name}
                    selectedItem={null}
                    id={table.id}
                    item={table}
                    mode="reservation"
                    tableStatus={String(availabilityInfo.status ?? "0")}
                    isReservationSelected={selected}
                    onTableClick={() => handleTableClick(table)}
                    tooltipText={[
                      `Mesa ${getTableLabel(table)}`,
                      enableMove
                        ? "Arrasta para ajustar a posição neste horário"
                        : "",
                      getReservationInfoText(availabilityInfo),
                    ]
                      .filter(Boolean)
                      .join(" — ")}
                  />
                </PositionedTable>
              );
            })}
          </FloorCanvas>
        </FloorCanvasViewport>
      </FloorScroll>

      <ZoomControls>
        <ZoomButton type="button" onClick={() => updateZoom(zoom - 0.1)}>
          −
        </ZoomButton>

        <ZoomSlider
          type="range"
          min="50"
          max="180"
          step="5"
          value={Math.round(zoom * 100)}
          onChange={(event) => updateZoom(Number(event.target.value) / 100)}
        />

        <ZoomButton type="button" onClick={() => updateZoom(zoom + 0.1)}>
          +
        </ZoomButton>

        <ZoomPercent>{Math.round(zoom * 100)}%</ZoomPercent>
      </ZoomControls>

      {unplacedTables.length > 0 && (
        <UnplacedBox>
          <UnplacedTitle>
            Mesas sem posição no mapa, mas ainda disponíveis para seleção:
          </UnplacedTitle>

          <UnplacedList>
            {unplacedTables.map((table) => {
              const availabilityInfo =
                availabilityById.get(String(table.id)) || {};

              const selected = isTableSelected(
                selectedTables,
                zoneName,
                table.id,
              );

              return (
                <UnplacedButton
                  key={table.id}
                  type="button"
                  selected={selected}
                  status={String(availabilityInfo.status ?? "0")}
                  onClick={() => handleTableClick(table)}
                  title={[
                    `Mesa ${getTableLabel(table)}`,
                    getReservationInfoText(availabilityInfo),
                  ]
                    .filter(Boolean)
                    .join(" — ")}
                >
                  {getTableLabel(table)}
                </UnplacedButton>
              );
            })}
          </UnplacedList>
        </UnplacedBox>
      )}
    </>
  );
}
